/**
 * Voice Chat Middleware
 *
 * Redux middleware that connects voice calls with the backend voice conversation storage
 * It listens for voice call events and creates/updates voice conversation records
 *
 * This middleware uses the upsert pattern:
 * - When a voice call starts, it calls the startVoiceCallForChat function
 * - The server handles checking if there's an existing active call for this conversation
 * - Only one active voice call can exist per conversation at a time
 * - This simplifies the middleware by eliminating complex error handling
 */

import * as types from "../../duck/types";
import {
  startVoiceCallForChat,
  endVoiceCallForChat,
} from "../services/voiceChatIntegrationService";

// Track debounce state to prevent multiple simultaneous calls
let startCallInProgress = false;
let endCallInProgress = false;
let callDebounceTimers = {};

// The voice chat middleware handles voice call events and persists them to the backend
const voiceChatMiddleware = (store) => (next) => (action) => {
  // First pass the action through to the reducer
  const result = next(action);

  // Extract current state for conversation ID
  const state = store.getState();
  const currentConversationId = state.chat?.selectedConversationId;

  // Handle voice call start with debounce
  if (action.type === types.VAPI_CALL_START && currentConversationId) {
    // Prevent multiple simultaneous call starts
    if (!startCallInProgress) {
      startCallInProgress = true;

      // Clear any ending call debounce
      if (callDebounceTimers.end) {
        clearTimeout(callDebounceTimers.end);
        callDebounceTimers.end = null;
      }

      (async () => {
        try {
          const callId = action.payload?.callId || "NA";
          const metadata = {
            assistantId: action.payload?.assistantId,
            startedAt: Date.now(),
            userName: state.user?.profile?.firstName,
          };

          await startVoiceCallForChat(currentConversationId, callId, metadata);
        } catch (error) {
          console.error("Failed to create/update voice conversation:", error);
        } finally {
          // Reset the flag after a delay
          callDebounceTimers.start = setTimeout(() => {
            startCallInProgress = false;
          }, 3000);
        }
      })();
    } else {
      console.warn("Blocked duplicate call start request");
    }
  }

  // Handle voice call end with debounce
  if (action.type === types.VAPI_CALL_END && currentConversationId) {
    // Prevent multiple simultaneous call ends
    if (!endCallInProgress) {
      endCallInProgress = true;

      // Clear any starting call debounce
      if (callDebounceTimers.start) {
        clearTimeout(callDebounceTimers.start);
        callDebounceTimers.start = null;
      }

      (async () => {
        try {
          console.log(
            "Ending voice call for conversation:",
            currentConversationId
          );
          await endVoiceCallForChat(currentConversationId);
        } catch (error) {
          console.error("Failed to close voice conversation:", error);
        } finally {
          // Reset the flag after a delay
          callDebounceTimers.end = setTimeout(() => {
            endCallInProgress = false;
          }, 3000);
        }
      })();
    } else {
      console.warn("Blocked duplicate call end request");
    }
  }

  return result;
};

export default voiceChatMiddleware;
